import { ExportIcon, ItemsIcon, ProductsIcon, ShirtIcon, VariantIcon } from "@xola/icons";
import { ButtonGroup, Tag, Tooltip } from "@xola/ui-kit";
import { useBoolean, useCreation, useMemoizedFn } from "ahooks";
import { JsDateRange, now } from "common";
import { isEqual } from "lodash";
import React, { useMemo, useRef, useState } from "react";
import { SelectionState } from "types";
import { NoTranslate } from "@/components/Common/NoTranslate";
import { SecondaryButton } from "@/components/Common/SecondaryButton";
import { ListFilter, TagTargetProps } from "@/components/Dashboard/Filters/ListFilter";
import { LedgerGrid, LedgerGridHandle } from "@/components/Reports/MerchandiseLedger/LedgerGrid";
import {
    CHANGE_TYPES,
    ChangeType,
    defaultRange,
    expandAllSelection,
    filterRowsByChangeType,
    useFetchWareProducts,
    useInventoryLedger,
    useLedgerUsers,
    useMerchandiseLedger,
} from "@/components/Reports/MerchandiseLedger/merchandiseLedgerContentHelpers";
import { ReportDatePicker } from "@/components/Reports/ReportDatePicker";
import { getSeller } from "@/utils/getSeller";

// An empty product selection reads as "None", not ListFilter's default "All": the ledger stays empty until a
// product is picked. `productNames` resolves a deep-link product that isn't on ListFilter's loaded page.
type ProductFilterTargetProps = TagTargetProps & { productNames: Record<string, string> };

const ProductFilterTarget = ({ value, onClose, onClick, productNames }: ProductFilterTargetProps) => {
    const { selected } = value;
    const summary =
        selected.length === 0
            ? "None"
            : selected.length === 1
            ? selected[0].name ?? productNames[selected[0].id] ?? selected[0].id
            : `${selected.length} selected`;

    return (
        <Tag
            color="primary"
            size="medium"
            className="items-center leading-5 hover:cursor-pointer hover:border-black hover:bg-gray-light"
            onClose={selected.length > 0 ? onClose : undefined}
            onClick={onClick}
        >
            <span className="mr-1 font-bold">Products:</span>
            <NoTranslate>{summary}</NoTranslate>
        </Tag>
    );
};

// Scoped (single-variant) view has no product-level audits, so it drops the "Product Changes" tab.
const SCOPED_CHANGE_TYPES: ChangeType[] = ["variant", "inventory", "all"];

interface TabMeta {
    label: string;
    tooltip: string;
    testId: string;
    Icon: React.ComponentType<{ size?: string }>;
}

const TAB_META: Record<ChangeType, TabMeta> = {
    product: {
        label: "Product",
        tooltip: "Product Changes",
        Icon: ProductsIcon,
        testId: "ledger-product-changes-button",
    },
    variant: {
        label: "Variant",
        tooltip: "Variant Changes",
        Icon: VariantIcon,
        testId: "ledger-variant-changes-button",
    },
    inventory: {
        label: "Inventory",
        tooltip: "Inventory Changes",
        Icon: ItemsIcon,
        testId: "ledger-inventory-changes-button",
    },
    all: { label: "All", tooltip: "All Changes", Icon: ShirtIcon, testId: "ledger-all-changes-button" },
};

// ButtonGroup (collapsed) introspects its ButtonGroup.Button children via React.Children to wire the active
// index and per-tab clicks, so the buttons must be direct children rather than a wrapper component.
const renderTabButtons = (types: ChangeType[], activeType: ChangeType) => {
    return types.map((type) => {
        const { label, tooltip, Icon, testId } = TAB_META[type];
        const isActive = type === activeType;

        return (
            <ButtonGroup.Button key={type} aria-label={tooltip} className="h-7.5 px-2.5 text-base" data-testid={testId}>
                <Tooltip content={tooltip} disabled={isActive} offset={[0, 18]}>
                    <span className="w-full space-x-1">
                        <Icon size="medium" /> {isActive && label}
                    </span>
                </Tooltip>
            </ButtonGroup.Button>
        );
    });
};

interface MerchandiseLedgerContentProps {
    // Merchandise mode: preselect this product; empty means the user picks one.
    initialProductId?: string;
    // Inventory mode: scope to a single variant (catalog item); changing the Product filter releases the scope.
    catalogItemId?: string;
}

export const MerchandiseLedgerContent = ({ initialProductId, catalogItemId }: MerchandiseLedgerContentProps) => {
    const [inventoryScoped, { setFalse: releaseInventoryScope }] = useBoolean(Boolean(catalogItemId));

    // One now() snapshot so range and the picker's dateValue can't straddle midnight and land a day apart.
    const initialRange = useCreation(defaultRange, []);
    const [range, setRange] = useState(initialRange);
    const [dateValue, setDateValue] = useState<JsDateRange>(() => ({
        from: initialRange.from.toDate(),
        to: initialRange.to.toDate(),
    }));
    const [products, setProducts] = useState<SelectionState>({
        all: false,
        selected: initialProductId ? [initialProductId] : [],
    });
    const [changeTypeIndex, setChangeTypeIndex] = useState(catalogItemId ? CHANGE_TYPES.indexOf("all") : 0);
    const changeType = CHANGE_TYPES[changeTypeIndex];

    const productIds = products.selected;
    const rangeParam = `${range.from.format("YYYY-MM-DD")},${range.to.format("YYYY-MM-DD")}`;
    const sellerId = getSeller().id;
    const merchandise = useMerchandiseLedger(productIds, sellerId, rangeParam, changeType);
    const inventory = useInventoryLedger(catalogItemId, sellerId, rangeParam, inventoryScoped);

    // Scoped rows carry the same Event Category, so the same Variant/Inventory split applies here.
    const scopedRows = useMemo(() => filterRowsByChangeType(inventory.rows, changeType), [inventory.rows, changeType]);

    const allRows = inventoryScoped ? scopedRows : merchandise.rows;
    const isLoading = inventoryScoped ? inventory.isLoading : merchandise.isLoading;
    const isError = inventoryScoped ? inventory.isError : merchandise.isError;
    const { userNames, userSources } = useLedgerUsers(allRows);

    // ListFilter resets its selection whenever `value` changes identity, so a fresh literal each render
    // would wipe the filter.
    const productFilterValue = useCreation<SelectionState>(
        () => (inventoryScoped ? { all: false, selected: inventory.productId ? [inventory.productId] : [] } : products),
        [inventoryScoped, inventory.productId, products],
    );
    // Names ListFilter can't resolve from its loaded page: the selected products (fetched by id) plus the
    // scoped variant's parent.
    const productLabelMap = useMemo(
        () => ({
            ...merchandise.productNames,
            ...(inventory.productId && inventory.productName ? { [inventory.productId]: inventory.productName } : {}),
        }),
        [merchandise.productNames, inventory.productId, inventory.productName],
    );
    const renderProductTarget = useMemoizedFn((props: TagTargetProps) => (
        <ProductFilterTarget {...props} productNames={productLabelMap} />
    ));
    // ListFilter fires onChange on every Apply, so ignore an unchanged selection: an accidental Apply would
    // otherwise discard the variant scope.
    const handleProductChange = useMemoizedFn((selection: SelectionState) => {
        const next = expandAllSelection(selection);
        if (isEqual(next.selected, productFilterValue.selected)) {
            return;
        }

        setProducts(next);
        if (inventoryScoped) {
            releaseInventoryScope();
            setChangeTypeIndex(CHANGE_TYPES.indexOf("inventory"));
        }
    });
    const handleClearProducts = useMemoizedFn(() => setProducts({ all: false, selected: [] }));

    const gridProductNames = inventoryScoped ? inventory.productNames : merchandise.rowProductNames;
    const gridVariantNames = inventoryScoped ? inventory.variantNames : merchandise.variantNames;
    const showInventoryColumns = inventoryScoped || changeType !== "product";
    const showChangeDetailColumns = changeType === "inventory" || changeType === "all";

    const handleRangeChange = useMemoizedFn((value: JsDateRange) => {
        setDateValue(value);
        if (value.from && value.to) {
            setRange({ from: now(value.from).startOf("day"), to: now(value.to).endOf("day") });
        }
    });

    const selectedProductNames = useMemo(
        () => products.selected.map((productId) => merchandise.productNames[productId] ?? productId).join(", "),
        [products.selected, merchandise.productNames],
    );

    const hasFilterTags = inventoryScoped || products.selected.length > 0;

    const tabTypes = inventoryScoped ? SCOPED_CHANGE_TYPES : CHANGE_TYPES;
    const tabValue = inventoryScoped ? Math.max(SCOPED_CHANGE_TYPES.indexOf(changeType), 0) : changeTypeIndex;
    const handleTabChange = useMemoizedFn((index: number) => {
        setChangeTypeIndex(inventoryScoped ? CHANGE_TYPES.indexOf(SCOPED_CHANGE_TYPES[index]) : index);
    });

    const gridRef = useRef<LedgerGridHandle>(null);
    const handleExport = useMemoizedFn(() => {
        const fileName = `Ledger_${changeType}_${range.from.format("YYYY-MM-DD")}_${range.to.format(
            "YYYY-MM-DD",
        )}.xlsx`;
        gridRef.current?.exportToExcel(fileName);
    });

    return (
        <div className="flex h-full w-full min-w-0 flex-col">
            <div className="flex flex-wrap items-center gap-2 border-b border-gray-light px-6 py-3">
                <ReportDatePicker
                    value={dateValue}
                    from={range.from}
                    to={range.to}
                    max={now()}
                    onChange={handleRangeChange}
                />
                <ListFilter
                    label="Product"
                    title="Filter Products"
                    fetch={useFetchWareProducts}
                    collection={["products"]}
                    value={productFilterValue}
                    show={{ search: true, selectAll: false }}
                    target={renderProductTarget}
                    onChange={handleProductChange}
                />
                <ButtonGroup isCollapsed size="small" value={tabValue} onChange={handleTabChange}>
                    {renderTabButtons(tabTypes, changeType)}
                </ButtonGroup>
                <SecondaryButton
                    size="small"
                    className="ml-auto"
                    icon={<ExportIcon />}
                    disabled={allRows.length === 0}
                    onClick={handleExport}
                >
                    Export
                </SecondaryButton>
            </div>

            {hasFilterTags && (
                <div className="flex flex-wrap items-center gap-2 border-b border-gray-light px-6 py-2">
                    <span className="font-bold">Filters:</span>
                    {inventoryScoped ? (
                        <>
                            {inventory.productName && (
                                <Tag>
                                    <span className="mr-1 font-bold">Product:</span>
                                    <NoTranslate>{inventory.productName}</NoTranslate>
                                </Tag>
                            )}
                            <Tag>
                                <span className="mr-1 font-bold">Variant:</span>
                                <NoTranslate>{inventory.variantName}</NoTranslate>
                            </Tag>
                        </>
                    ) : (
                        products.selected.length > 0 && (
                            <Tag onClose={handleClearProducts}>
                                <span className="mr-1 font-bold">Products:</span>
                                <NoTranslate>{selectedProductNames}</NoTranslate>
                            </Tag>
                        )
                    )}
                </div>
            )}

            <div className="min-h-0 flex-1 p-6">
                <LedgerGrid
                    ref={gridRef}
                    rows={allRows}
                    userNames={userNames}
                    userSources={userSources}
                    productNames={gridProductNames}
                    variantNames={gridVariantNames}
                    showInventoryColumns={showInventoryColumns}
                    showChangeDetailColumns={showChangeDetailColumns}
                    isLoading={isLoading}
                    isError={isError}
                    emptyMessage={
                        !inventoryScoped && productIds.length === 0 ? "Select a product to view its ledger." : undefined
                    }
                />
            </div>
        </div>
    );
};
